import {
    ApolloError,
    useLazyQuery,
    useMutation,
    useQuery,
    useReactiveVar,
} from '@apollo/client'
import {
    AuthorBaseRoles,
    Role,
} from '@deltek/specification-client/core/Configurations/Authorization'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ConstructionBudgetsUpdatedValues } from '@deltek/specification-client/core/Constants/constructionBudgets'
import NavPrompt, {
    NavPromptMessageProps,
} from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import {
    CurrentUserActiveFirmQuery,
    CurrentUserInfoQuery,
} from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import SlideOutModal from '@deltek/specification-client/core/Modal/SlideOutModal'
import { AssociationModifierList } from '@deltek/specification-client/core/Models/associationModifierList'
import {
    CurrentUserActiveFirmResponse,
    CurrentUserInfoResponse,
} from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    FirmId,
    FirmResponse,
} from '@deltek/specification-client/core/Models/firm'
import { nameValuePropType } from '@deltek/specification-client/core/Models/nameValuePropType'
import { Theme } from '@material-ui/core/styles'
import makeStyles from '@material-ui/core/styles/makeStyles'
import {
    CreateProjectMutation,
    UpdateProjectMutation,
} from 'api/graphQL/mutations/project.mutations'
import { FirmProjectParametersQuery } from 'api/graphQL/queries/firm.queries'
import { updateOwnerId } from 'api/graphQL/reactiveVars/projectHome'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { CreateProjectResult, Project } from 'Models/project'
import { ProjectInput } from 'models/projectInput'
import React from 'react'
import { availableProjectParamters } from 'shared/utils/projectParameterUtils'
import ProjectGroupDialog from '../ProjectsHomePage/components/ProjectGroupDialog'
import ProjectOwnerDialog from '../ProjectsHomePage/components/ProjectOwnerDialog'
import NewProject, {
    multiSelectNameValuePropType,
    NewProjectFormProps,
} from './NewProject'

// @ts-ignore 
const useStyles = makeStyles((theme: Theme) => ({
    drawerPaper: {
        backgroundColor: '#fff',
        width: 560,
        zIndex: theme.zIndex.modal + 2,
        borderLeft: 0,
    },
    drawerWrapper: {
        '& > .MuiBackdrop-root': {
            zIndex: `${theme.zIndex.modal + 1} !important`,
        }
    }
}))

const levelOfSpecificationValues = ['100', '200', '300', '350', '400', '500']

interface NewProjectSlideOutProps {
    open: boolean
    onClose: VoidFunction
    triggerProjectListReload: VoidFunction
    setSkipCount?: (val: number) => void
    projectData?: Partial<Project> | undefined
}

const defaultFormState: NewProjectFormProps = {
    projectGroup: {
        value: '',
        required: true,
    },
    projectNumber: {
        value: '',
        required: true,
    },
    projectName: {
        value: '',
        required: true,
    },
    constructionBudget: {
        value: 'Not Listed',
        required: true,
    },
    owner: {
        value: '',
        required: false,
    },
    grossBuildingArea: {
        value: '',
        required: false,
    },
    issueFor: {
        value: '',
        required: false,
    },
    addendumNumber: {
        value: '',
        required: false,
    },
    projectDate: {
        value: '',
        required: false,
    },
    issueDate: {
        value: '',
        required: false,
    },
    street1: {
        value: '',
        required: true,
        maxLength: 100,
    },
    street2: {
        value: '',
        required: false,
        maxLength: 100,
    },
    country: {
        value: 'United States',
        required: true,
    },
    city: {
        value: '',
        required: true,
        maxLength: 100,
    },
    postalCode: {
        value: '',
        required: true,
        maxLength: 10,
    },
    region: {
        value: '',
        required: true,
        maxLength: 100,
    },
    projectPhase: {
        value: '',
        required: true,
    },
    levelOfSpecification: {
        value: '',
        required: true,
    },
    phaseStartDate: {
        value: '',
        required: false,
    },
    phaseEndDate: {
        value: '',
        required: false,
    },
    id: { value: '', required: false },

}

const leavingPageMessage =
    'Are you sure you want to navigate away from this page? Your changes will not be saved.\n\nSelect OK to continue, or Cancel to stay on the current page.'

const NewProjectSlideOut: React.FC<NewProjectSlideOutProps> = ({
    open,
    onClose,
    triggerProjectListReload,
    setSkipCount,
    projectData,
}) => {
    const classes = useStyles()
    const formStateRef = React.useRef<NewProjectFormProps>(defaultFormState)
    const [dirtyForm, toggleFormDirty] = React.useState<boolean>(false)

    const [projectGroups, setProjectGroups] = React.useState<
        Record<nameValuePropType, string>[] | null
    >([])
    const [
        associationModifierLists,
        setassociationModifierLists,
    ] = React.useState<
        Record<multiSelectNameValuePropType, string | boolean>[] | null
    >([])
    const [
        firmAssociationModifierLists,
        setfirmAssociationModifierLists,
    ] = React.useState<Partial<AssociationModifierList>[] | null>([])

    const [projectPhases, setProjectPhases] = React.useState<
        Record<nameValuePropType, string>[] | null
    >([])

    const [levelOfSpecifications] = React.useState<
        Record<nameValuePropType, string>[] | null
    >(
        levelOfSpecificationValues.map((value) => ({
            name: value,
            value: value,
        }))
    )
    const [
        isConfirmationDialogOpen,
        setIsConfirmationDialogOpen,
    ] = React.useState<boolean>(false)

    const [
        isLevelOfSpecificationChangeConfirmed,
        setIsLevelOfSpecificationChangeConfirmed,
    ] = React.useState<boolean>(false)

    const [
        isProjectGroupDialogOpen,
        setIsProjectGroupDialogOpen,
    ] = React.useState<boolean>(false)

    const [defaultProjectGroup, setDefaultProjectGroup] = React.useState<Record<
        nameValuePropType,
        string
    > | null>()

    const [defaultProjectOwner, setDefaultProjectOwner] = React.useState<
        string | null | undefined
    >()

    const toggleAddProjectGroupDialog = () => {
        setIsProjectGroupDialogOpen(!isProjectGroupDialogOpen)
    }

    const [
        isProjectOwnerDialogOpen,
        setIsProjectOwnerDialogOpen,
    ] = React.useState<boolean>(false)

    const toggleAddProjectOwnerDialog = () => {
        if (!isProjectOwnerDialogOpen) {
            updateOwnerId('')
        }
        setIsProjectOwnerDialogOpen(!isProjectOwnerDialogOpen)
    }

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )

    const { data: currentUserActiveFirmResponse } = useQuery<
        CurrentUserActiveFirmResponse
    >(CurrentUserActiveFirmQuery)

    const { isAdmin } = useReactiveVar(userInfo)

    const activeFirmId: string = React.useMemo(() => {
        return currentUserActiveFirmResponse?.myActiveFirm.firmId ?? ''
    }, [currentUserActiveFirmResponse])

    const firmId = currentUserInfoResponse?.currentUserInfo.account.accountId

    const isOwnerDisabled: boolean = React.useMemo(() => {
        return AuthorBaseRoles.includes(
            (currentUserInfoResponse?.currentUserInfo?.roles[0] ?? '') as Role
        )
    }, [currentUserInfoResponse])

    const {
        loading: firmProjectGroupLoading,
        error: firmProjectGroupError,
    } = useQuery<FirmResponse, FirmId>(FirmProjectParametersQuery, {
        variables: {
            firmId:
                activeFirmId && activeFirmId !== ''
                    ? activeFirmId
                    : firmId ?? '',
        },
        onCompleted: (data) => {
            const projectPhases = (data?.firm?.projectPhases || []).map(
                (projectPhase) => {
                    return {
                        value: projectPhase.id!,
                        name: projectPhase.name!,
                    } as Record<nameValuePropType, string>
                }
            )
            setProjectPhases(projectPhases)

            const projectGroups = (data?.firm?.projectGroups || [])?.map(
                (projectGroup) => {
                    return {
                        value: projectGroup.id!,
                        name: projectGroup.name!,
                    } as Record<nameValuePropType, string>
                }
            )
            setProjectGroups(projectGroups)

            const associationModifierLists: Record<
                multiSelectNameValuePropType,
                string | boolean
            >[] = []

                ; (data.firm.associationModifierLists || []).forEach(
                    (associationModifierList: Partial<AssociationModifierList>) => {
                        const availableProjectParamterIndex = availableProjectParamters.findIndex(
                            (parameter) =>
                                parameter.name === associationModifierList.name!
                        )
                        if (availableProjectParamterIndex >= 0) {
                            associationModifierLists.push({
                                value: associationModifierList.id!,
                                name:
                                    availableProjectParamters[
                                        availableProjectParamterIndex
                                    ].label,
                                multiple:
                                    availableProjectParamters[
                                        availableProjectParamterIndex
                                    ].multiple,
                            } as Record<multiSelectNameValuePropType, string | boolean>)

                            formStateRef.current[associationModifierList.id!] = {
                                value: '',
                                required: true,
                            }
                        }
                    }
                )

            associationModifierLists.sort(
                (first, second) =>
                    availableProjectParamters.findIndex(
                        (paramter) => paramter.label === first.name
                    ) -
                    availableProjectParamters.findIndex(
                        (paramter) => paramter.label === second.name
                    )
            )

            setassociationModifierLists(associationModifierLists)
            setfirmAssociationModifierLists(
                data.firm.associationModifierLists || []
            )
        },
    })

    const [getProjectGroups] = useLazyQuery<FirmResponse, FirmId>(
        FirmProjectParametersQuery,
        {
            variables: {
                firmId: activeFirmId ?? '',
            },
            onCompleted: (data) => {
                const projectGroups = (data.firm.projectGroups || []).map(
                    (projectGroup) => {
                        return {
                            value: projectGroup.id!,
                            name: projectGroup.name!,
                        } as Record<nameValuePropType, string>
                    }
                )
                setProjectGroups(projectGroups)
                setDefaultProjectGroup(projectGroups[projectGroups.length - 1])
            },
        }
    )

    const [getAssociationModifiers] = useLazyQuery<FirmResponse, FirmId>(
        FirmProjectParametersQuery,
        {
            variables: {
                firmId: firmId ?? '',
            },
            onCompleted: (data) => {
                const associationModifierLists: Record<
                    multiSelectNameValuePropType,
                    string | boolean
                >[] = []

                    ; (data.firm.associationModifierLists || []).forEach(
                        (
                            associationModifierList: Partial<
                                AssociationModifierList
                            >
                        ) => {
                            const availableProjectParamterIndex = availableProjectParamters.findIndex(
                                (parameter) =>
                                    parameter.name === associationModifierList.name!
                            )
                            if (availableProjectParamterIndex >= 0) {
                                associationModifierLists.push({
                                    value: associationModifierList.id!,
                                    name:
                                        availableProjectParamters[
                                            availableProjectParamterIndex
                                        ].label,
                                    multiple:
                                        availableProjectParamters[
                                            availableProjectParamterIndex
                                        ].multiple,
                                } as Record<multiSelectNameValuePropType, string | boolean>)

                                formStateRef.current[
                                    associationModifierList.id!
                                ] = {
                                    value: '',
                                    required: true,
                                }
                            }
                        }
                    )

                associationModifierLists.sort(
                    (first, second) =>
                        availableProjectParamters.findIndex(
                            (paramter) => paramter.label === first.name
                        ) -
                        availableProjectParamters.findIndex(
                            (paramter) => paramter.label === second.name
                        )
                )

                setassociationModifierLists(associationModifierLists)
                setfirmAssociationModifierLists(
                    data.firm.associationModifierLists || []
                )

                if (data.firm.associationModifierLists) {
                    data.firm.associationModifierLists.forEach(
                        (el: Partial<AssociationModifierList>) => {
                            if (el && el.name === 'Owner Type') {
                                if (
                                    el.associationModifiers &&
                                    el.associationModifiers.length > 0
                                ) {
                                    setDefaultProjectOwner(
                                        el.associationModifiers[
                                            el.associationModifiers.length - 1
                                        ].id
                                    )
                                }
                            }
                        }
                    )
                }
            },
        }
    )

    const [getProjectGroupList] = useLazyQuery<FirmResponse, FirmId>(
        FirmProjectParametersQuery,
        {
            variables: {
                firmId: activeFirmId ?? '',
            },
            onCompleted: (data) => {
                const projectGroups = (data.firm.projectGroups || []).map(
                    (projectGroup) => {
                        return {
                            value: projectGroup.id!,
                            name: projectGroup.name!,
                        } as Record<nameValuePropType, string>
                    }
                )
                setProjectGroups(projectGroups)
            },
        }
    )

    const [
        createProject,
        {
            loading: createProjectLoading,
            error: createProjectError,
            data: createProjectData,
        },
    ] = useMutation<
        { createProject: CreateProjectResult | null },
        { createProjectInput: ProjectInput }
    >(CreateProjectMutation, {
        onError: (error: ApolloError) => {
            showGlobalSnackbar({
                variables: {
                    id: 'NewProjectSlideOut - createProject',
                    variant: 'error',
                    message: 'Error creating new project',
                },
            })
        },
    })

    const [
        updateProject,
        {
            loading: updateProjectLoading,
            error: updateProjectError,
            data: updateProjectData,
        },
    ] = useMutation<
        { updateProject: Partial<Project> | null },
        { updateProjectInput: ProjectInput }
    >(UpdateProjectMutation, {
        onError: (error: ApolloError) => {
            showGlobalSnackbar({
                variables: {
                    id: 'NewProjectSlideOut - updateProject',
                    variant: 'error',
                    message: 'Error updating project',
                },
            })
        },
    })

    const handleCloseSlideOut = () => {
        if (
            dirtyForm &&
            !isConfirmationDialogOpen &&
            !isLevelOfSpecificationChangeConfirmed
        ) {
            NavPromptDialogContainer(
                JSON.stringify({
                    title: 'Projects Page',
                    preferredMessage: leavingPageMessage,
                    buttonColor: '#a78966',
                    confirmText: 'OK',
                    cancelText: 'Cancel',
                } as NavPromptMessageProps),
                (callbackState: boolean) => {
                    if (callbackState) {
                        onClose()
                        toggleFormDirty(false)
                        setDefaultProjectGroup(null)
                        setDefaultProjectOwner(null)
                        if (!!createProjectData!)
                            createProjectData!.createProject = null
                        if (!!updateProjectData!)
                            updateProjectData!.updateProject = null
                    }
                }
            )
        } else {
            if (!isConfirmationDialogOpen) {
                onClose()
            }
            toggleFormDirty(false)
            if (!!createProjectData!) createProjectData!.createProject = null
            if (!!updateProjectData!) updateProjectData!.updateProject = null
        }
    }

    const getModifiers = (associationModifierListsId: string) => {
        const firmAssociationModifierList = firmAssociationModifierLists?.find(
            (item) => item.id === associationModifierListsId
        )
        return (firmAssociationModifierList?.associationModifiers || []).map(
            (modifier) => {
                return { value: modifier.id, name: modifier.name } as Record<
                    nameValuePropType,
                    string
                >
            }
        )
    }

    React.useEffect(() => {
        if (!!projectData) {
            formStateRef.current.id.value = projectData.id

            formStateRef.current.projectGroup.value = projectGroups?.find(
                (group) => group.name === projectData.groupName
            )?.value
            formStateRef.current.projectNumber.value = projectData.number
            formStateRef.current.projectName.value = projectData.name
            formStateRef.current.constructionBudget.value = projectData.budget
            formStateRef.current.grossBuildingArea.value =
                projectData.grossBuildingArea
            formStateRef.current.addendumNumber.value =
                projectData.addendumNumber
            formStateRef.current.issueFor.value = projectData.issueFor
            formStateRef.current.projectDate.value = projectData.projectDate
            formStateRef.current.issueDate.value = projectData?.issueDate
            formStateRef.current.owner.value = projectData.owner
            formStateRef.current.street1.value = projectData.location?.street1
            formStateRef.current.street2.value = projectData.location?.street2
            formStateRef.current.country.value = projectData.location?.country
            formStateRef.current.city.value = projectData.location?.city
            formStateRef.current.postalCode.value =
                projectData.location?.postalCode
            formStateRef.current.region.value = projectData.location?.region

            formStateRef.current.levelOfSpecification.value = levelOfSpecifications?.find(
                (los) => los.name === projectData.levelOfSpecification
            )?.value

            if ((projectData.milestones || []).length > 0) {
                formStateRef.current.projectPhase.value = projectData.milestones![0].phaseId
                formStateRef.current.phaseStartDate.value = projectData.milestones![0].startDate
                formStateRef.current.phaseEndDate.value = projectData.milestones![0].endDate
            }

            projectData.parameters?.forEach((parameter) => {
                if (!!formStateRef.current[parameter.modifierList?.id!]) {
                    formStateRef.current[
                        parameter.modifierList?.id!
                    ].value = parameter
                        .selectedModifiers!.map((modifier) => modifier.id)
                        .join(',')
                }
            })
        } else {
            for (const [key] of Object.entries(formStateRef.current)) {
                formStateRef.current[key].value = ''
                if (key === 'country') {
                    formStateRef.current[key].value = 'United States'
                }
                if (key === 'constructionBudget') {
                    formStateRef.current[key].value = 'Not Listed'
                }
            }
        }
    }, [projectData, projectGroups, formStateRef])

    React.useEffect(() => {
        if (open) {
            getProjectGroupList()
            setIsProjectGroupDialogOpen(false)
        }
    }, [open])

    const slideOutProps = {
        open,
        onClose: handleCloseSlideOut,
        classes: { paper: classes.drawerPaper },
    }

    return (
        <span className={classes.drawerWrapper}>
            {open && (
                <SlideOutModal {...slideOutProps}>
                    <NewProject
                        toggleFormDirty={toggleFormDirty}
                        showGlobalSnackbar={showGlobalSnackbar}
                        projectGroups={projectGroups!}
                        levelOfSpecifications={levelOfSpecifications!}
                        firmProjectGroupLoading={firmProjectGroupLoading}
                        firmProjectGroupError={firmProjectGroupError!}
                        constructionBudgets={ConstructionBudgetsUpdatedValues}
                        onCancel={handleCloseSlideOut}
                        associationModifierLists={associationModifierLists!}
                        getModifiers={getModifiers}
                        defaultFormState={formStateRef.current}
                        isEditMode={formStateRef.current.id.value !== ''}
                        firmId={activeFirmId!}
                        createProjectLoading={createProjectLoading}
                        createProjectError={createProjectError!}
                        createProjectData={createProjectData!}
                        createProject={createProject}
                        updateProjectLoading={updateProjectLoading}
                        updateProjectError={updateProjectError!}
                        updateProjectData={updateProjectData!}
                        updateProject={updateProject}
                        projectPhases={projectPhases!}
                        triggerProjectListReload={triggerProjectListReload}
                        setSkipCount={setSkipCount}
                        openProjectGroupDialog={toggleAddProjectGroupDialog}
                        openProjectOwnerDialog={toggleAddProjectOwnerDialog}
                        defaultProjectGroup={defaultProjectGroup}
                        defaultProjectOwner={defaultProjectOwner}
                        isAdmin={isAdmin}
                        isOwnerDisabled={isOwnerDisabled}
                        isLevelOfSpecificationConfirmationDialogOpen={
                            isConfirmationDialogOpen
                        }
                        isLevelOfSpecificationChangeConfirmed={
                            isLevelOfSpecificationChangeConfirmed
                        }
                        setIsLevelOfSpecificationConfirmationDialogOpen={
                            setIsConfirmationDialogOpen
                        }
                        setIsLevelOfSpecificationChangeConfirmed={
                            setIsLevelOfSpecificationChangeConfirmed
                        }
                    />
                    {isProjectGroupDialogOpen && (
                        <ProjectGroupDialog
                            openDialog={isProjectGroupDialogOpen}
                            handleDialogClose={() =>
                                toggleAddProjectGroupDialog()
                            }
                            refetch={getProjectGroups}
                        />
                    )}
                    {isProjectOwnerDialogOpen && (
                        <ProjectOwnerDialog
                            openDialog={isProjectOwnerDialogOpen}
                            handleDialogClose={() =>
                                toggleAddProjectOwnerDialog()
                            }
                            refetch={getAssociationModifiers}
                        />
                    )}
                </SlideOutModal>
            )}
            <NavPrompt
                showPrompt={dirtyForm && !isConfirmationDialogOpen}
                preferredMessage={leavingPageMessage}
                confirmText="OK"
                cancelText="Cancel"
                title="Projects Page"
                buttonColor="#a78966"
            />
        </span>
    )
}
export default NewProjectSlideOut
